import React, { useState } from 'react';
import { ArrowLeft, Phone, Mail, MapPin, Clock, Send, MessageSquare, Headphones } from 'lucide-react';
import { Link } from 'react-router-dom';

function ContactUs() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // In a real application, you would send this to your backend
    console.log('Form submitted:', formData);
    setSubmitted(true);
    setTimeout(() => {
      setSubmitted(false);
      setFormData({ name: '', email: '', phone: '', subject: '', message: '' });
    }, 3000);
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-6xl mx-auto px-4">
        {/* Back Button */}
        <Link to="/" className="inline-flex items-center gap-2 text-orange-600 hover:text-orange-700 mb-6 transition-colors">
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm font-semibold">Back to Home</span>
        </Link>

        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm p-8 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <MessageSquare className="w-8 h-8 text-orange-600" />
            <h1 className="text-3xl font-bold text-slate-900">Contact Us</h1>
          </div>
          <p className="text-slate-600">We're here to help! Reach out to us for any questions or support.</p>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {/* Contact Form */}
          <div className="md:col-span-2 bg-white rounded-lg shadow-sm p-8">
            <h2 className="text-xl font-bold text-slate-900 mb-6">Send Us a Message</h2>
            
            {submitted ? (
              <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
                <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-white text-2xl">✓</span>
                </div>
                <h3 className="text-lg font-bold text-green-900 mb-2">Message Sent Successfully!</h3>
                <p className="text-green-700 text-sm">
                  Thank you for contacting us. We'll get back to you within 24 hours.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      Full Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                      placeholder="Enter your full name"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      Email Address <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                      placeholder="your@email.com"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      Phone Number <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                      placeholder="+91 XXXXX XXXXX"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">
                      Subject <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                    >
                      <option value="">Select a subject</option>
                      <option value="order">Order Related Query</option>
                      <option value="product">Product Information</option>
                      <option value="payment">Payment & Coupons</option>
                      <option value="return">Returns & Refunds</option>
                      <option value="technical">Technical Support</option>
                      <option value="feedback">Feedback & Suggestions</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">
                    Message <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows="5"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                    placeholder="Tell us how we can help you..."
                  ></textarea>
                </div>

                <button
                  type="submit"
                  className="w-full bg-orange-600 hover:bg-orange-700 text-white py-3 rounded-lg font-semibold flex items-center justify-center gap-2 transition-colors"
                >
                  <Send className="w-4 h-4" />
                  Send Message
                </button>
              </form>
            )}
          </div>

          {/* Contact Information */}
          <div className="space-y-6">
            {/* Quick Contact */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center gap-2 mb-4">
                <Headphones className="w-5 h-5 text-orange-600" />
                <h2 className="text-lg font-bold text-slate-900">Quick Contact</h2>
              </div>
              
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <Phone className="w-5 h-5 text-orange-600 mt-1 shrink-0" />
                  <div>
                    <p className="text-sm font-semibold text-slate-900">Phone</p>
                    <a href="tel:+917353297211" className="text-sm text-slate-700 hover:text-orange-600">
                      +91 73532 97211
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Mail className="w-5 h-5 text-orange-600 mt-1 shrink-0" />
                  <div>
                    <p className="text-sm font-semibold text-slate-900">Email</p>
                    <a href="mailto:info@brightplanetventures.com" className="text-sm text-slate-700 hover:text-orange-600 break-all">
                      info@brightplanetventures.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Clock className="w-5 h-5 text-orange-600 mt-1 shrink-0" />
                  <div>
                    <p className="text-sm font-semibold text-slate-900">Business Hours</p>
                    <p className="text-sm text-slate-700">
                      Monday - Saturday<br />
                      9:00 AM - 6:00 PM IST
                    </p>
                    <p className="text-xs text-slate-500 mt-1">Sunday: Closed</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Our Locations */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <div className="flex items-center gap-2 mb-4">
                <MapPin className="w-5 h-5 text-orange-600" />
                <h2 className="text-lg font-bold text-slate-900">Our Locations</h2>
              </div>
              
              <div className="space-y-4">
                <div className="pb-3 border-b border-gray-100">
                  <p className="text-xs font-bold text-orange-600 uppercase mb-1">Bailhongal HQ</p>
                  <p className="text-xs text-slate-700 leading-relaxed">
                    Mulla Complex, Somwar Pethe<br />
                    Near MalaPrabha Hospital<br />
                    Bailhongal - 591102
                  </p>
                </div>

                <div className="pb-3 border-b border-gray-100">
                  <p className="text-xs font-bold text-orange-600 uppercase mb-1">Gokak Branch</p>
                  <p className="text-xs text-slate-700 leading-relaxed">
                    Navarang Plaza<br />
                    Opposite Masurkar Hospital<br />
                    Naka No.1, Gokak - 591307
                  </p>
                </div>

                <div>
                  <p className="text-xs font-bold text-orange-600 uppercase mb-1">Kittur Hub</p>
                  <p className="text-xs text-slate-700 leading-relaxed">
                    Kittur - 591115, Karnataka
                  </p>
                </div>
              </div>
            </div>

            {/* GST Info */}
            <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg p-4">
              <p className="text-xs text-slate-600 mb-1">GST Number</p>
              <p className="text-sm font-bold text-slate-900">29AAKCD7090A1ZL</p>
            </div>
          </div>
        </div>

        {/* FAQ Quick Links */}
        <div className="mt-6 bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-lg font-bold text-slate-900 mb-4">Frequently Asked Questions</h2>
          <div className="grid md:grid-cols-2 gap-4 text-sm">
            <Link to="/return-policy" className="text-orange-600 hover:text-orange-700 flex items-center gap-2">
              <span>→</span> How do I return a product?
            </Link>
            <Link to="/refund-policy" className="text-orange-600 hover:text-orange-700 flex items-center gap-2">
              <span>→</span> When will I receive my refund?
            </Link>
            <Link to="/account" className="text-orange-600 hover:text-orange-700 flex items-center gap-2">
              <span>→</span> How do I track my order?
            </Link>
            <Link to="/coupons" className="text-orange-600 hover:text-orange-700 flex items-center gap-2">
              <span>→</span> How do coupons work?
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ContactUs;
